import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  Save,
  CheckCircle,
  Image as ImageIcon,
  RefreshCw,
  Upload,
  Loader2,
  Trash2,
  Archive,
  Eye,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import {
  adminGetArticle,
  adminPatchArticle,
  adminRegenImage,
  adminUploadImage,
  adminDeleteArticle,
  resolveImage,
} from "@/lib/blogApi";
import ReviseArticleModal from "@/components/ReviseArticleModal";

const RED = "#D41F30";

export default function AdminArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenImg, setRegenImg] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);

  // Editable fields
  const [title, setTitle] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [category, setCategory] = useState("");
  const [bodyMd, setBodyMd] = useState("");

  useEffect(() => {
    adminGetArticle(id)
      .then((a) => {
        setArticle(a);
        setTitle(a.title || "");
        setSeoTitle(a.seo_title || "");
        setMetaDesc(a.meta_description || "");
        setCategory(a.category || "");
        setBodyMd(a.body_md || "");
      })
      .catch((e) => {
        if (e.status === 401 || e.status === 403) {
          navigate("/login", { replace: true });
          return;
        }
        setError(e.message);
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const save = async (extraPatch = {}) => {
    setSaving(true);
    setError("");
    try {
      const updated = await adminPatchArticle(id, {
        title,
        seo_title: seoTitle,
        meta_description: metaDesc,
        category,
        body_md: bodyMd,
        ...extraPatch,
      });
      setArticle(updated);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const publish = () => save({ status: "published" });
  const unpublish = () => save({ status: "pending_review" });
  const archive = () => save({ status: "archived" });

  const handleRegenImage = async () => {
    setRegenImg(true);
    setError("");
    try {
      const { image_url } = await adminRegenImage(id);
      setArticle((a) => ({ ...a, image_url }));
    } catch (e) {
      setError(`Image generation failed: ${e.message}`);
    } finally {
      setRegenImg(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const { image_url } = await adminUploadImage(id, file);
      setArticle((a) => ({ ...a, image_url }));
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this article permanently?")) return;
    await adminDeleteArticle(id).catch(() => {});
    navigate("/admin", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400" data-testid="editor-loading">
        Loading article...
      </div>
    );
  }
  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        Article not found.
      </div>
    );
  }

  const isPublished = article.status === "published";

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-editor">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-4">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-[#D41F30]"
            data-testid="editor-back"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>

          <div className="flex items-center gap-2">
            {savedAt && !saving && (
              <span className="text-xs text-gray-400 hidden sm:inline">
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => setReviseOpen(true)}
              data-testid="editor-revise"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg border border-[#D41F30]/30 text-[#D41F30] hover:bg-red-50"
            >
              <Sparkles className="w-4 h-4" /> Revise with AI
            </button>
            <button
              onClick={() => setPreview((p) => !p)}
              data-testid="editor-preview-toggle"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Eye className="w-4 h-4" /> {preview ? "Edit" : "Preview"}
            </button>
            <button
              onClick={() => save()}
              disabled={saving}
              data-testid="editor-save"
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-full border-2 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
            {isPublished ? (
              <>
                <Link
                  to={`/blog/${article.slug}`}
                  target="_blank"
                  data-testid="editor-view-live"
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <ExternalLink className="w-4 h-4" /> View
                </Link>
                <button
                  onClick={unpublish}
                  data-testid="editor-unpublish"
                  disabled={saving}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-full border-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  Unpublish
                </button>
              </>
            ) : (
              <button
                onClick={publish}
                disabled={saving}
                data-testid="editor-publish"
                className="inline-flex items-center gap-1 px-5 py-2 text-sm font-semibold rounded-full bg-[#D41F30] text-white hover:bg-[#B91C2A]"
              >
                <CheckCircle className="w-4 h-4" /> Publish
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="editor-error">
              {error}
            </div>
          )}

          {!preview ? (
            <>
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mb-2">
                  Title
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="field-title"
                  className="w-full font-display text-2xl md:text-3xl font-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 rounded-lg px-2 py-1"
                  placeholder="Article title"
                />
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mb-2">
                  Body (Markdown)
                </label>
                <textarea
                  value={bodyMd}
                  onChange={(e) => setBodyMd(e.target.value)}
                  data-testid="field-body"
                  className="w-full min-h-[600px] font-mono text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 rounded-lg px-3 py-2 leading-relaxed"
                  placeholder="## Section heading..."
                />
                <p className="mt-2 text-xs text-gray-400">
                  Em dashes are auto-stripped on save. Use ## for sections and ### for FAQ questions.
                </p>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10">
              <h1 className="font-display text-3xl md:text-4xl font-black text-gray-900 mb-6 leading-tight">
                {title}
              </h1>
              <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-gray-900 prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-blockquote:border-l-4 prose-blockquote:border-[#D41F30] prose-blockquote:bg-red-50 prose-blockquote:rounded-r-2xl prose-blockquote:py-2 prose-blockquote:px-5 prose-blockquote:not-italic prose-a:text-[#D41F30]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyMd}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {/* Image */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-3">
              Featured image
            </div>
            {article.image_url ? (
              <div className="aspect-[16/10] rounded-lg overflow-hidden bg-gray-100 mb-3">
                <img
                  src={resolveImage(article.image_url)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-[16/10] rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-sm mb-3">
                No image
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleRegenImage}
                disabled={regenImg}
                data-testid="image-regen"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {regenImg ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Regenerate with AI
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleUpload}
                data-testid="image-upload-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="image-upload-btn"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload custom
              </button>
            </div>
          </div>

          {/* SEO */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
            <div className="text-xs font-bold tracking-wider uppercase text-gray-500">
              SEO
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">SEO title</label>
              <input
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                data-testid="field-seo-title"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Meta description ({metaDesc.length}/160)
              </label>
              <textarea
                value={metaDesc}
                onChange={(e) => setMetaDesc(e.target.value)}
                data-testid="field-meta"
                rows={3}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Category</label>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="field-category"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30"
              />
            </div>
            <div className="text-xs text-gray-400 pt-1">
              Slug: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{article.slug}</code>
              <br />
              Keywords:{" "}
              <span className="text-gray-600">
                {[article.primary_keyword, ...(article.secondary_keywords || [])].filter(Boolean).join(", ")}
              </span>
            </div>
          </div>

          {/* Danger zone */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-3">
              Actions
            </div>
            <div className="flex flex-col gap-2">
              {article.status !== "archived" && (
                <button
                  onClick={archive}
                  data-testid="editor-archive"
                  className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
                >
                  <Archive className="w-4 h-4" /> Archive
                </button>
              )}
              <button
                onClick={handleDelete}
                data-testid="editor-delete"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" /> Delete forever
              </button>
            </div>
          </div>
        </aside>
      </main>

      <ReviseArticleModal
        open={reviseOpen}
        articleId={id}
        onClose={() => setReviseOpen(false)}
        onComplete={(updated) => {
          setArticle(updated);
          setBodyMd(updated.body_md || "");
          setReviseOpen(false);
        }}
      />
    </div>
  );
}
